import { errorToast } from "@/v2-ui/toast"
import {
  FEATURE_CONTENT_DECISION,
  FEATURE_SURFACE_DECISION,
  type FeatureContentDecisionValue,
  type FeatureSurfaceDecisionValue,
} from "@/v2-backoffice-client/license/changes/license.changes.constants"
import { useFeatureSurfaceDecideMutation } from "@/v2-backoffice-client/license/changes/license.changes.decide.mutation"
import type { LicenseDecisionWrite } from "@/v2-backoffice-client/license/changes/license.changes.decision.types"
import { useFeatureContentDecideMutation } from "@/v2-backoffice-client/license/changes/license.content.decide.mutation"
import {
  evictFeatureContentDecisionQueries,
  evictFeatureSurfaceDecisionQueries,
} from "@/v2-backoffice-client/license/license.utils"

// The control's words as the surface mutation's enum spells them (string
// literals typed as the enum's values, the userIdentity.constants pattern).
const SURFACE_DECISION_BY_WRITE: Record<
  LicenseDecisionWrite,
  FeatureSurfaceDecisionValue
> = {
  everyone: FEATURE_SURFACE_DECISION.everyone as FeatureSurfaceDecisionValue,
  packages: FEATURE_SURFACE_DECISION.packages as FeatureSurfaceDecisionValue,
  undecided: FEATURE_SURFACE_DECISION.undecided as FeatureSurfaceDecisionValue,
}

/**
 * A board's write for one feature: resolves to whether it was saved. The
 * hook reports its own error, so a caller only says what happened on
 * success.
 */
export type LicenseDecisionWriterByCode = (
  code: string,
  decision: LicenseDecisionWrite,
  packageIds?: number[],
) => Promise<boolean>

/**
 * The one write behind every capability decision, whether it is taken in
 * the capability panel or from a row's menu: decideFeatureSurface, with
 * the cache evictions that bring the board, the tab count, the package
 * editor and the feature panels back in line.
 */
export function useCapabilityDecisionWriter() {
  const [decide, { loading: isSaving }] = useFeatureSurfaceDecideMutation({
    update: evictFeatureSurfaceDecisionQueries,
    onError(error) {
      errorToast(error, { title: "Failed to save the decision" })
    },
  })

  const write: LicenseDecisionWriterByCode = async (
    surfaceCode,
    decision,
    featureGroupIds,
  ) => {
    const result = await decide({
      variables: {
        surfaceCode,
        decision: SURFACE_DECISION_BY_WRITE[decision],
        featureGroupIds,
      },
    })
    return Boolean(result.data)
  }

  return { write, isSaving }
}

/**
 * The same for map content (a layer or base map): decideFeatureContent.
 * Content has no "undecided" to go back to, so that write is refused.
 */
export function useContentDecisionWriter() {
  const [decide, { loading: isSaving }] = useFeatureContentDecideMutation({
    update: evictFeatureContentDecisionQueries,
    onError(error) {
      errorToast(error, { title: "Failed to save the decision" })
    },
  })

  const write: LicenseDecisionWriterByCode = async (
    featureCode,
    decision,
    featureGroupIds,
  ) => {
    if (decision === "undecided") return false
    const result = await decide({
      variables: {
        featureCode,
        decision: (decision === "packages"
          ? FEATURE_CONTENT_DECISION.packages
          : FEATURE_CONTENT_DECISION.everyone) as FeatureContentDecisionValue,
        featureGroupIds,
      },
    })
    return Boolean(result.data)
  }

  return { write, isSaving }
}
